import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../../../core/constants/colors';
import { billingService } from '../../services/billing.service';
import { SubscriptionPlan } from '../../types/billing.types';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgradeComplete?: () => void;
}

export const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  onUpgradeComplete,
}) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadPlans();
    }
  }, [isOpen]);

  const loadPlans = async () => {
    try {
      setIsLoading(true);
      const plansData = await billingService.getPlans();
      setPlans(plansData.filter((plan) => plan.is_active));
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatAmount = (amount: string, currency: string) => {
    try {
      const numAmount = parseFloat(amount);
      if (isNaN(numAmount)) return amount;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numAmount);
    } catch {
      return `${currency} ${amount}`;
    }
  };

  const handleUpgrade = () => {
    onClose();
    navigate('/dashboard/billing');
    if (onUpgradeComplete) {
      onUpgradeComplete();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: COLORS.user.primaryLight }}>
                  <svg
                    className="w-6 h-6"
                    style={{ color: COLORS.user.primary }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Upgrade Required</h2>
                  <p className="text-sm text-gray-600">This feature requires a premium subscription</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                To access this premium feature, please upgrade to one of our subscription plans.
              </p>

              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div
                    className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-transparent"
                    style={{ borderColor: COLORS.user.primary, borderTopColor: 'transparent' }}
                  />
                </div>
              ) : plans.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No subscription plans available at the moment.</p>
                </div>
              ) : (
                <div className="space-y-3 mb-6">
                  {plans
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .slice(0, 3)
                    .map((plan) => (
                      <div
                        key={plan.id}
                        className="p-4 rounded-lg border hover:shadow-md transition-shadow cursor-pointer"
                        style={{ borderColor: '#E5E7EB' }}
                        onClick={handleUpgrade}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-gray-900">{plan.name}</h3>
                              {plan.is_featured && (
                                <span
                                  className="px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                                  style={{ backgroundColor: COLORS.extended.orange }}
                                >
                                  Featured
                                </span>
                              )}
                            </div>
                            {plan.description && (
                              <p className="text-sm text-gray-600">{plan.description}</p>
                            )}
                          </div>
                          <div className="text-right ml-4">
                            <p className="text-lg font-bold" style={{ color: COLORS.user.primary }}>
                              {formatAmount(plan.amount, plan.currency)}
                            </p>
                            <p className="text-xs text-gray-500">
                              /{plan.duration_days >= 30 ? `${Math.floor(plan.duration_days / 30)} mo` : `${plan.duration_days} days`}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 rounded-lg border font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                style={{ borderColor: '#E5E7EB' }}
              >
                Maybe Later
              </button>
              <button
                type="button"
                onClick={handleUpgrade}
                className="flex-1 px-4 py-3 rounded-lg font-semibold text-white transition-all hover:opacity-90"
                style={{ backgroundColor: COLORS.user.primary }}
              >
                View All Plans
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
